import React, { createContext, useContext, useState, useEffect } from 'react';

interface Asset {
  asset_id: string;
  asset_name: string;
  ISO: string;
  asset_type: string;
  icon: string;
  RT_VOL: string | null;
  DA_VOL: string | null;
  RT_LMP: string;
  DA_LMP: string;
  gasBurnValue: string;
  cyclePercentage: string | null;
  batteryLevel: string | null;
  windspeed: string | null;
  unit_status: string | null;
  reg_status: string | null;
  sunriseTime: string;
  sunsetTime: string;
  Day1_Forecast: string | null;
  Day2_Forecast: string | null;
  revenuePeriod?: string;
}

interface DataContextProps {
  children: React.ReactNode;
}

interface DataContextType {
  assetData: Asset[];
  setAssetData: React.Dispatch<React.SetStateAction<Asset[]>>;
  filters: {
    selectedAssetType: string;
    selectedISO: string;
    sortField: string;
    sortOrder: string;
    groupByField: string;
  };
  setFilters: React.Dispatch<React.SetStateAction<{
    selectedAssetType: string;
    selectedISO: string;
    sortField: string;
    sortOrder: string;
    groupByField: string;
  }>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>; 
}


const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<DataContextProps> = ({ children }) => {
  const [assetData, setAssetData] = useState<Asset[]>(() => {
    // Check if assetData exists in sessionStorage
    const storedData = localStorage.getItem('assetData');
    return storedData ? JSON.parse(storedData) : [];
  });
  const [searchQuery, setSearchQuery] = useState<string>(() => {
    return sessionStorage.getItem('searchQuery') || '';
  });
  
  useEffect(() => {
    sessionStorage.setItem('searchQuery', searchQuery);
  }, [searchQuery]);
  
  const [filters, setFilters] = useState(() => {
    const storedFilters = sessionStorage.getItem("filters");
    return storedFilters
      ? JSON.parse(storedFilters)
      : {
          selectedAssetType: "",
          selectedISO: "",
          sortField: "",
          sortOrder: "Ascending",
          groupByField: "",
        };
  });
  
  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem("filters", JSON.stringify(filters));
  }, [filters]);
  
  useEffect(() => {
    // Whenever assetData changes, save it to sessionStorage
    if (assetData.length > 0) {
      localStorage.setItem('assetData', JSON.stringify(assetData));
    }
  }, [assetData]);
  const [error, setError] = useState<string | null>(null);

  return (
    <DataContext.Provider value={{ assetData, setAssetData, filters, setFilters,error,setError,searchQuery, setSearchQuery }}>
      {children}
    </DataContext.Provider>
  );
};

export const useDataContext = (): DataContextType => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};
